import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { BookingStatus } from '@/types/BookingStatus.ts';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';
import { TourConsentPreviewDto } from '@/features/public/booking/types/TourConsentPreviewDto.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { AccountType } from '@/features/dashboard/paymentConfig/types/AccountType.ts';
import { ConsentStatus } from '@/features/dashboard/consentTemplates/types/ConsentStatus.ts';
import { ConsentType } from '@/features/dashboard/consentTemplates/types/ConsentTemplateType.ts';

export interface BookingPublicSummaryDto {
  bookingId: string;
  bookingCode: string;
  bookingStatus: BookingStatus;
  participantCount: number;
  client: PublicClientDto;
  participants: PublicSummaryParticipantDto[];
  /** Pozycje bez przypisania do osoby — zniżki całej rezerwacji i potrącenia, np. za rezygnację. */
  bookingAdjustments: PublicCostLineDto[];
  totalPrice: MoneyValueDto[];
  installments: InstallmentSummaryDto[];
  tourName: string;
  tourStartDate: string;
  tourEndDate: string;
  organizerName: string;
  organizerLogoUrl: string | null;
  chatLink?: string;
  consents: BookingSummaryConsentDto[];
  cashInstruction?: string;
  transferAccounts: TransferAccountSummaryDto[];
  transactionRef: string;
}

/**
 * Osoba, która założyła rezerwację i płaci — na jej własnym podsumowaniu.
 *
 * Dane bez maskowania: to jej dane, które sama wpisała, a strona otwiera się tokenem tej
 * rezerwacji. Sens kafla jest ten sam co przy uczestniku — literówka w adresie e-mail znaczy, że
 * wiadomości od organizatora nie dochodzą.
 */
export interface PublicClientDto {
  firstName: string;
  lastName: string;
  email: string;
  /** ISO-8601 (`LocalDate`). */
  birthDate: string;
  phoneNumber: { dialCode: string; number: string } | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
}

/** Rodzaj pozycji rachunku — po nim strona układa sekcje kafla. */
export enum PublicCostLineKind {
  BASE = 'BASE',
  OPTION = 'OPTION',
  TRANSPORT = 'TRANSPORT',
  DISCOUNT = 'DISCOUNT',
  MANUAL = 'MANUAL',
}

/**
 * Wiersz rachunku: co to jest i ile kosztuje. `value` niosą wyłącznie pozycje wybierane
 * z katalogu — `label` mówi wtedy „co", a `value` „które".
 */
export interface PublicCostLineDto {
  kind: PublicCostLineKind;
  label: string;
  value: string | null;
  /** Kwota w walucie tej pozycji — rachunek bywa wielowalutowy i kwot się nie sumuje. */
  amount: MoneyValueDto;
}

/** Odpowiedź z formularza zgłoszeniowego, gotowa do pokazania. */
export interface PublicFormAnswerDto {
  /** Panel zmian dopina po nim wartość sprzed zmiany — etykieta do tego nie wystarcza. */
  fieldId: string;
  label: string;
  value: string;
}

/**
 * Uczestnik na podsumowaniu — to samo, co organizator ma na swoim kaflu.
 *
 * Uczestnika po rezygnacji tu nie ma: jego pozycje są w księdze unieważnione, a potrącenie stoi
 * w `bookingAdjustments`.
 */
export interface PublicSummaryParticipantDto {
  id: string;
  firstName: string;
  lastName: string;
  /** ISO-8601 (`LocalDate`). */
  birthDate: string;
  totalPrice: MoneyValueDto[];
  costLines: PublicCostLineDto[];
  formAnswers: PublicFormAnswerDto[];
  insuranceName: string | null;
}

export interface TransferAccountSummaryDto {
  currency: Currency;
  bankName: string;
  accountNumber: string;
  accountType: AccountType;
  swiftCode?: string;
  companyAddress?: string;
}

export interface InstallmentSummaryDto {
  dueDate: string;
  allowedPaymentMethods: PaymentMethod[];
  transactions: TransactionSummaryDto[];
}

export interface TransactionSummaryDto {
  plannedTransactionId: string;
  amount: MoneyValueDto;
  paymentStatus: PlannedTransactionPaymentStatus;
}

export interface BookingSummaryConsentDto {
  tourConsentId: string;
  acceptedConsentId: string | null;
  type: ConsentType;
  name: string;
  status: ConsentStatus;
  signable: boolean;
}
